import { jsx as _jsx } from "react/jsx-runtime";
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import deepmerge from 'deepmerge';
import { Provider, useDispatch, useSelector, useStore } from 'react-redux';
import PersistState from './persist-state';
import RemoteState from './remote-state';
import { agentConfig } from '../clients/agent';
import page, { pageInitialState } from './slices/page';
import popup, { popupInitialState } from './slices/popup';
import remote, { remoteInitialState } from './slices/remote';
import site, { siteInitialState } from './slices/site';
import status, { statusInitialState } from './slices/status';
import widget, { widgetInitialState } from './slices/widget';
import { useDeepEqualSelector } from '../utils/use-deep-equal-selector';
import { setLeaveStoreFlush } from '../utils/on-leave';
export const persistKey = 'jaenjs-state';
const agent = agentConfig();
const { loadState, persistState, persistMiddleware } = PersistState(persistKey, {
    dropCatalogue: Boolean(agent)
});
const combinedReducer = combineReducers({
    site,
    page,
    status,
    popup,
    widget,
    remote
});
const remoteState = agent && typeof window !== 'undefined' ? RemoteState(agent) : null;
const rootReducer = (state, action) => {
    if (action.type === 'RESET_STATE') {
        const payload = action.payload || {};
        return {
            site: deepmerge(siteInitialState, payload.site || {}),
            page: deepmerge(pageInitialState, payload.page || {}),
            status: statusInitialState,
            popup: deepmerge(popupInitialState, payload.popup || {}),
            widget: deepmerge(widgetInitialState, payload.widget || {}),
            remote: {
                ...remoteInitialState,
                active: state?.remote?.active ?? false,
                revision: state?.remote?.revision,
                publishedRevision: state?.remote?.publishedRevision,
                connection: state?.remote?.connection ?? 'poll',
                authors: state?.remote?.authors ?? {}
            }
        };
    }
    return combinedReducer(state, action);
};
const persistedState = loadState();
export const store = configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware => getDefaultMiddleware({
        thunk: { extraArgument: {} }
    }).concat([
        ...persistMiddleware,
        ...(remoteState ? [remoteState.recordMiddleware] : [])
    ]),
    devTools: true || process.env.NODE_ENV !== 'production',
    preloadedState: persistedState
});
const persister = persistState(store);
export const { resetState } = persister;
setLeaveStoreFlush(persister.flush);
export const disconnectRemote = remoteState ? remoteState.connect(store) : null;
export const jaenAgent = agent;
export const useAppDispatch = () => useDispatch();
export const useAppSelector = useSelector;
export const useAppDeepEqualSelector = useDeepEqualSelector;
export const useAppState = () => useStore().getState();
export const withRedux = (Component) => props => {
    return (_jsx(Provider, { store: store, children: _jsx(Component, { ...props }) }));
};
